import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Confirm-and-cascade delete of a single profile.
 * - Loads preview on open (impact counts + blockers)
 * - Blocks deletion when profile has accepted applications on posted gigs
 * - Cascade-removes profile + posted gigs + made/received applications + reviews
 */
export default function DeleteProfileDialog({ open, onOpenChange, profile, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !profile?.profile_id) return;
    setPreview(null);
    setLoading(true);
    api.get(`/profiles/${profile.profile_id}/delete-preview`)
      .then(({ data }) => setPreview(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load delete preview"))
      .finally(() => setLoading(false));
  }, [open, profile?.profile_id]);

  const onConfirm = async () => {
    if (!profile?.profile_id) return;
    setDeleting(true);
    try {
      await api.delete(`/profiles/${profile.profile_id}`);
      toast.success(`${profile.display_name} deleted`);
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete profile");
    } finally {
      setDeleting(false);
    }
  };

  const blocked = preview && !preview.can_delete;
  const rows = preview
    ? [
        { label: "Posted gigs", value: preview.gigs_posted },
        { label: "Applications received on your gigs", value: preview.applications_received },
        { label: "Applications you sent from this profile", value: preview.applications_made },
        { label: "Reviews written", value: preview.reviews_written },
        { label: "Reviews received", value: preview.reviews_received },
      ]
    : [];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-profile-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            Delete {profile?.display_name}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-slate-600 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 py-4 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking what will be removed…
                </div>
              )}

              {blocked && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900" data-testid="delete-blocked">
                  <div className="font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Cannot delete this profile yet
                  </div>
                  <div className="mt-1">
                    This profile has {preview.blocking_accepted_applications.length} accepted application(s) on posted gigs.
                    Cancel or complete those gigs first.
                  </div>
                  <ul className="mt-2 list-disc pl-5 text-xs">
                    {preview.blocking_accepted_applications.slice(0, 5).map((a) => (
                      <li key={a.application_id}>
                        <span className="font-medium">{a.opportunity_title}</span>
                        {a.applicant_name ? ` — ${a.applicant_name}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview && !blocked && (
                <>
                  <div>
                    This will <span className="font-semibold text-slate-900">permanently remove</span> the profile and all data tied to it. Your account and other profiles are safe.
                  </div>
                  <ul className="rounded-md border border-slate-200 divide-y divide-slate-100 text-sm">
                    {rows.map((r) => (
                      <li key={r.label} className="flex items-center justify-between px-3 py-2">
                        <span>{r.label}</span>
                        <span className="font-semibold text-slate-900">{r.value}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-xs text-red-600">This action cannot be undone.</div>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="delete-confirm"
            disabled={loading || deleting || blocked}
            onClick={(e) => { e.preventDefault(); onConfirm(); }}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white"
          >
            {deleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>) : "Delete permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
